import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Markdown from "react-markdown";
import { HelpCircle, X, Send, Loader2, ThumbsUp, ThumbsDown, RotateCcw, BookOpen } from "lucide-react";
import { api, API_BASE, tokenStore } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useHelpSection } from "@/components/HelpButton";

const SESSION_KEY = "mjp_helpchat_session";
const MSGS_KEY = "mjp_helpchat_messages";

const SUGGESTIONS = {
  dashboard: ["Hoe begin ik met een nieuwe club?", "Wat zie ik in de proefperiode?"],
  "voorgaande-jaren": ["Wat is het verschil tussen KOSTEN en Overige kosten?", "Hoe importeer ik mijn jaarverslag als PDF?"],
  afschrijvingen: ["Hoe werkt afschrijven met een startdatum?", "Wat doe ik met volledig afgeschreven activa?"],
  "bestaande-leningen": ["Annuïteit of lineair — wat is het verschil?", "Wat is de DSCR?"],
  scenariobouwer: ["Wat betekent NPV en welke disconteringsvoet kies ik?", "Hoe voer ik een investering met lening in?"],
  scenariorapport: ["Waarom daalt mijn kaspositie in dit scenario?", "Hoe lees ik de liquiditeitsopbouw?"],
  vergelijken: ["Hoe vergelijk ik een basis- en voorzichtig scenario?"],
  account: ["Hoe zeg ik mijn abonnement op?", "Waar vind ik mijn facturen?"],
  default: ["Waarom is kasstroom belangrijker dan resultaat?", "Wat betekent NPV?", "Hoe maak ik een begroting?"],
};

const mdComponents = {
  a: ({ node, href = "", ...p }) => <a href={href} target={href.startsWith("/handleiding") || href.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className="underline text-primary" {...p} />,
  p: ({ node, ...p }) => <p className="my-1.5 leading-relaxed" {...p} />,
  ul: ({ node, ...p }) => <ul className="my-1.5 pl-4 list-disc space-y-0.5" {...p} />,
  ol: ({ node, ...p }) => <ol className="my-1.5 pl-4 list-decimal space-y-0.5" {...p} />,
  strong: ({ node, ...p }) => <strong className="font-bold" {...p} />,
  code: ({ node, ...p }) => <code className="font-mono-num text-[12px] bg-muted px-1" {...p} />,
  h1: ({ node, ...p }) => <p className="font-bold mt-2" {...p} />,
  h2: ({ node, ...p }) => <p className="font-bold mt-2" {...p} />,
  h3: ({ node, ...p }) => <p className="font-bold mt-2" {...p} />,
};

const load = (k, fb) => { try { return JSON.parse(sessionStorage.getItem(k)) ?? fb; } catch { return fb; } };
const save = (k, v) => { try { sessionStorage.setItem(k, JSON.stringify(v)); } catch { /* ignore */ } };

function useRouteContext() {
  const { pathname } = useLocation();
  const m = pathname.match(/^\/clubs\/([^/]+)(?:\/scenarios\/([^/]+))?/);
  const scenarioId = m?.[2] && !["new"].includes(m[2]) ? m[2] : null;
  return { clubId: m?.[1] || null, scenarioId };
}

export default function HelpChat() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const section = useHelpSection();
  const { clubId, scenarioId } = useRouteContext();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => load(MSGS_KEY, []));
  const [sessionId, setSessionId] = useState(() => load(SESSION_KEY, null));
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [cookieVisible, setCookieVisible] = useState(false);
  const listRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => { save(MSGS_KEY, messages); }, [messages]);
  useEffect(() => { save(SESSION_KEY, sessionId); }, [sessionId]);
  useEffect(() => {
    const check = () => { try { setCookieVisible(!localStorage.getItem("mjp_cookie_notice_v1")); } catch { setCookieVisible(false); } };
    check();
    const t = setInterval(check, 1500);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (open) listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const suggestions = useMemo(() => SUGGESTIONS[section] || SUGGESTIONS.default, [section]);

  const send = useCallback(async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "", streaming: true }]);
    const token = tokenStore.get();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await fetch(`${API_BASE}/help/chat`, {
        method: "POST",
        credentials: "include",
        signal: ctrl.signal,
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ message, session_id: sessionId, page: pathname, page_anchor: section, club_id: clubId, scenario_id: scenarioId }),
      });
      if (!res.ok) {
        let detail = "Er ging iets mis.";
        try { detail = (await res.json()).detail || detail; } catch { /* ignore */ }
        setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, text: detail, error: true, streaming: false } : x)));
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buffer = "";
      let acc = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += dec.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.split("\n").find((l) => l.startsWith("data: "));
          if (!line) continue;
          const ev = JSON.parse(line.slice(6));
          if (ev.type === "start") { setSessionId(ev.session_id); }
          else if (ev.type === "delta") {
            acc += ev.content;
            const snapshot = acc;
            setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, text: snapshot } : x)));
          } else if (ev.type === "error") {
            setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, text: ev.detail, error: true } : x)));
          } else if (ev.type === "done") {
            setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, streaming: false, index: ev.index, sessionId: ev.session_id } : x)));
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setMessages((m) => m.map((x, i) => (i === m.length - 1 ? { ...x, text: "Verbinding verbroken. Probeer het opnieuw.", error: true, streaming: false } : x)));
      }
    } finally {
      setMessages((m) => m.map((x) => ({ ...x, streaming: false })));
      setBusy(false);
    }
  }, [input, busy, sessionId, pathname, section, clubId, scenarioId]);

  const feedback = async (i, value) => {
    const msg = messages[i];
    if (!msg?.sessionId || msg.index == null) return;
    const next = msg.feedback === value ? null : value;
    setMessages((m) => m.map((x, j) => (j === i ? { ...x, feedback: next } : x)));
    try { await api.post("/help/feedback", { session_id: msg.sessionId, index: msg.index, value: next }); } catch { /* stil */ }
  };

  const reset = () => { abortRef.current?.abort(); setMessages([]); setSessionId(null); setInput(""); };

  if (!user) return null;

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="helpchat-open" aria-label="Hulp & uitleg openen"
          className={`fixed right-5 z-40 btn-brutal bg-primary text-primary-foreground w-14 h-14 rounded-full inline-flex items-center justify-center print:hidden transition-[bottom] ${cookieVisible ? "bottom-20 md:bottom-28" : "bottom-5"}`}>
          <HelpCircle className="w-8 h-8" strokeWidth={2.5} />
        </button>
      )}
      {open && (
        <aside data-testid="helpchat-panel" className="fixed inset-0 sm:inset-auto sm:bottom-5 sm:right-5 sm:w-[400px] sm:h-[min(640px,calc(100vh-2.5rem))] z-50 bg-card border-2 border-foreground heavy-shadow flex flex-col print:hidden">
          <header className="flex items-center justify-between gap-2 px-4 py-3 border-b-2 border-foreground bg-foreground text-background">
            <div className="flex items-center gap-2 min-w-0">
              <HelpCircle className="w-4 h-4 shrink-0" />
              <div className="min-w-0">
                <div className="font-display font-black uppercase tracking-wide text-sm leading-tight">Hulp & uitleg</div>
                <div className="text-[10px] opacity-70 truncate">Antwoorden op basis van de handleiding{clubId ? " en je geopende club" : ""}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <a href={`/handleiding${section ? `#${section}` : ""}`} target="_blank" rel="noopener noreferrer" title="Handleiding openen" data-testid="helpchat-manual-link" className="p-1.5 hover:bg-background/20"><BookOpen className="w-4 h-4" /></a>
              <button onClick={reset} title="Nieuw gesprek" data-testid="helpchat-reset" className="p-1.5 hover:bg-background/20"><RotateCcw className="w-4 h-4" /></button>
              <button onClick={() => setOpen(false)} aria-label="Sluiten" data-testid="helpchat-close" className="p-1.5 hover:bg-background/20"><X className="w-4 h-4" /></button>
            </div>
          </header>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm" data-testid="helpchat-messages">
            {messages.length === 0 && (
              <div className="space-y-3" data-testid="helpchat-empty">
                <p className="text-muted-foreground">Stel een vraag over de app of over begrippen als kasstroom, NPV of DSCR. Ik antwoord op basis van de handleiding{scenarioId ? " en de cijfers van het geopende scenario" : clubId ? " en de gegevens van de geopende club" : ""}.</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="helpchat-suggestion" className="border-2 border-foreground px-2.5 py-1 text-xs text-left hover:bg-foreground hover:text-background transition-colors">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"} data-testid={`helpchat-msg-${m.role}`}>
                <div className={`max-w-[88%] px-3 py-2 border-2 ${m.role === "user" ? "bg-primary text-primary-foreground border-foreground" : m.error ? "bg-destructive/10 border-destructive text-destructive" : "bg-background border-foreground"}`}>
                  {m.role === "user" ? <p className="whitespace-pre-wrap">{m.text}</p> : (
                    <>
                      {m.text ? <Markdown components={mdComponents}>{m.text}</Markdown> : <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                      {m.streaming && m.text && <span className="inline-block w-1.5 h-3.5 bg-foreground animate-pulse ml-0.5 align-middle" />}
                      {!m.streaming && !m.error && m.index != null && (
                        <div className="flex gap-1 mt-1.5 -mb-0.5 justify-end">
                          <button onClick={() => feedback(i, "up")} aria-label="Nuttig" data-testid="helpchat-thumbs-up" className={`p-1 ${m.feedback === "up" ? "text-positive" : "text-muted-foreground hover:text-foreground"}`}><ThumbsUp className="w-3.5 h-3.5" /></button>
                          <button onClick={() => feedback(i, "down")} aria-label="Niet nuttig" data-testid="helpchat-thumbs-down" className={`p-1 ${m.feedback === "down" ? "text-destructive" : "text-muted-foreground hover:text-foreground"}`}><ThumbsDown className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="border-t-2 border-foreground p-3">
            <div className="flex gap-2 items-end">
              <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} maxLength={2000} placeholder="Typ je vraag… (Enter = versturen)" data-testid="helpchat-input"
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                className="flex-1 resize-none border-2 border-foreground bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              <button type="submit" disabled={busy || !input.trim()} data-testid="helpchat-send" aria-label="Versturen"
                className="btn-brutal bg-primary text-primary-foreground w-11 h-11 inline-flex items-center justify-center disabled:opacity-40">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-2 leading-snug">Controleer belangrijke cijfers altijd in het rapport. Gesprekken worden 90 dagen bewaard om de hulp te verbeteren.</p>
          </form>
        </aside>
      )}
    </>
  );
}
